import { ExternalLink, Landmark, AlertCircle, RefreshCw, Loader2 } from "lucide-react";

const STATUS_BADGE = {
  may_be_eligible: "bg-emerald-50 text-emerald-800 border-emerald-100",
  unknown: "bg-slate-100 text-slate-700 border-slate-200",
  unlikely: "bg-amber-50 text-amber-800 border-amber-100",
};

function ProgramCard({ program }) {
  const status = program.status || "unknown";
  return (
    <article
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_4px_18px_rgb(0,0,0,0.03)]"
      data-testid={`gov-program-${program.key}`}
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-medium text-slate-900" data-testid={`gov-program-${program.key}-name`}>
            {program.name}
          </h3>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">{program.summary}</p>
        </div>
        <span
          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${STATUS_BADGE[status]}`}
          data-testid={`gov-program-${program.key}-status`}
        >
          {program.status_label}
        </span>
      </header>

      {Array.isArray(program.reasons) && program.reasons.length > 0 ? (
        <ul className="mt-4 space-y-1.5">
          {program.reasons.map((r, i) => (
            <li
              key={`${program.key}-${i}-${String(r).slice(0, 24)}`}
              className="flex items-start gap-2 text-sm text-slate-600"
              data-testid={`gov-program-${program.key}-reason-${i}`}
            >
              <span className="mt-1.5 inline-block h-1 w-1 flex-none rounded-full bg-slate-400" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <span
          className="text-xs text-slate-500"
          data-testid={`gov-program-${program.key}-caveat`}
        >
          {program.caveat}
        </span>
        {program.official_source?.url ? (
          <a
            href={program.official_source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:text-sky-900"
            data-testid={`gov-program-${program.key}-link`}
          >
            {program.official_source.label || "Official source"}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

export default function GovernmentSupportSection({
  review,
  countryConfigured,
  onRescreen,
  rescreening,
  rescreenError,
}) {
  // Always show the disclaimer at the top of this section.
  return (
    <section
      className="mt-10 rounded-3xl border border-slate-200 bg-white p-7 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid="career-report-government-support"
    >
      <header className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
          <Landmark className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
            Government support
          </p>
          <h2 className="text-2xl font-medium text-slate-900">
            {review?.country
              ? `Programs to look into${review.country ? ` (${review.country})` : ""}`
              : "Programs to look into"}
          </h2>
        </div>
        {onRescreen ? (
          <button
            type="button"
            onClick={onRescreen}
            disabled={rescreening}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            data-testid="gov-rescreen-button"
          >
            {rescreening ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {rescreening ? "Rescreening…" : "Re-screen"}
          </button>
        ) : null}
      </header>

      <div
        className="mt-2 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
        data-testid="gov-disclaimer"
      >
        <AlertCircle className="h-4 w-4 mt-0.5 flex-none" />
        <div>
          <p>
            <strong>{review?.disclaimer || "This is not financial or legal advice."}</strong>
          </p>
          <p className="mt-1">
            {`We screen against publicly available eligibility hints — `}
            <strong>{review?.eligible_label || "You may be eligible"}</strong>
            {` for some of these programs. Always `}
            <strong>{review?.caveat || "Check official source"}</strong>
            {` to confirm your situation.`}
          </p>
        </div>
      </div>

      {rescreenError ? (
        <div
          className="mt-4 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
          data-testid="gov-rescreen-error"
        >
          {rescreenError}
        </div>
      ) : null}

      {!countryConfigured ? (
        <p
          className="mt-6 text-sm text-slate-500 italic"
          data-testid="gov-no-country"
        >
          Add your country in the Government Support section of the assessment to see programs you may qualify for.
        </p>
      ) : Array.isArray(review?.programs) && review.programs.length === 0 ? (
        <div className="mt-6" data-testid="gov-no-programs">
          {review?.note ? (
            <p className="text-sm text-slate-600 leading-relaxed">{review.note}</p>
          ) : (
            <p className="text-sm text-slate-500 italic">
              No programs surfaced for your country yet.
            </p>
          )}
        </div>
      ) : (
        <div
          className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="gov-programs-grid"
        >
          {(review?.programs || []).map((p) => (
            <ProgramCard key={p.key} program={p} />
          ))}
        </div>
      )}
    </section>
  );
}
